// Bibliothèque multimarque : recherche dans les extraits (référence exacte en tête) + liste filtrée des documents.
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, FileText, FlaskConical, Search, Upload } from 'lucide-react';
import { api } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { date, pluriel } from '../../lib/format.ts';
import {
  Badge, Bouton, Chargement, ChampChoix, EntetePage, EtatErreur, EtatVide, LienBouton, MarqueIa, Panneau, Surligne,
} from '../../components/ui.tsx';
import { BadgeStatutDocument, STATUTS_DOCUMENT } from '../../components/statuts.tsx';
import { NavDocumentation, TYPES_BIBLIOTHEQUE, TYPES_DOCUMENT, libelleType } from './commun.tsx';

interface LigneDocument {
  id: string; title: string; doc_type: string; manufacturer: string | null; range_name: string | null; product_refs: string[];
  versions_covered: string | null; published_on: string | null; demo: boolean; version_id: string | null; version_label: string | null;
  status: string | null; collected_at: string | null; last_checked_at: string | null; page_count: number | null; nb_versions: number;
}
interface ReponseListe { total: number; page: number; taille: number; documents: LigneDocument[]; fabricants: string[] }
interface Extrait {
  chunk_id: string; version_id: string; document_id: string; title: string; doc_type: string; manufacturer: string | null; range_name: string | null;
  version_label: string; status: string; page_no: number; locator: string | null; extrait: string; exact: boolean; ocr?: boolean;
}

const TAILLE = 50;

export function PageBibliotheque() {
  const { peut } = useSession();
  const [params, setParams] = useSearchParams();
  const ref = params.get('ref') ?? '';
  const statut = params.get('statut') ?? '';
  const fabricant = params.get('fabricant') ?? '';
  const type = params.get('type') ?? '';
  const q = params.get('q') ?? '';
  const page = Math.max(1, Number(params.get('page')) || 1);
  const nonValides = params.get('nonValides') !== '0';

  // Saisie de la recherche (validée par le bouton ou Entrée)
  const [saisie, setSaisie] = useState(q || ref);
  useEffect(() => { setSaisie(q || ref); }, [q, ref]);

  const changer = (maj: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(maj)) { if (v) p.set(k, v); else p.delete(k); }
    if (!('page' in maj)) p.delete('page');
    setParams(p, { replace: true });
  };

  const liste = useQuery({
    queryKey: ['documents', { ref, statut, fabricant, type, page }],
    enabled: peut('doc.lire'),
    queryFn: () => api.get<ReponseListe>(`/api/documents?${new URLSearchParams({
      ...(ref ? { ref } : {}), ...(statut ? { statut } : {}), ...(fabricant ? { fabricant } : {}), ...(type ? { type } : {}),
      page: String(page), taille: String(TAILLE) })}`),
    placeholderData: (p) => p,
  });

  const termeRecherche = q || ref;
  const recherche = useQuery({
    queryKey: ['documents-recherche', { q, ref, fabricant, nonValides }],
    enabled: peut('doc.lire') && !!termeRecherche,
    queryFn: () => api.get<{ resultats: Extrait[] }>(`/api/documents/recherche?${new URLSearchParams({
      ...(q ? { q } : {}), ...(ref ? { ref } : {}), ...(fabricant ? { fabricant } : {}), nonValides: nonValides ? '1' : '0' })}`),
  });

  const chercher = (e: FormEvent) => {
    e.preventDefault();
    const t = saisie.trim();
    // Une saisie d'un seul mot est cherchée comme texte ET comme référence exacte par le serveur.
    changer({ q: t || null, ref: null });
  };

  if (!peut('doc.lire')) {
    return <Panneau><EtatVide titre="Accès non autorisé" texte="Vos droits ne permettent pas de consulter la documentation." /></Panneau>;
  }

  const docs = liste.data?.documents ?? [];
  const nbPages = liste.data ? Math.max(1, Math.ceil(liste.data.total / TAILLE)) : 1;
  const filtresActifs = !!(ref || statut || fabricant || type);

  return (
    <div className="pile-l doc-module">
      <EntetePage titre="Documentation"
        sousTitre="Bibliothèque multimarque : fiches produits, notices, manuels, codes défaut. Chaque extrait s’ouvre à la bonne page."
        actions={peut('doc.importer') && <LienBouton to="/documentation/importer" variante="principal" icone={<Upload aria-hidden />}>Importer des documents</LienBouton>} />
      <NavDocumentation peutImporter={peut('doc.importer')} />

      <Panneau>
        <form className="doc-recherche" onSubmit={chercher} role="search">
          <label className="champ" style={{ flex: 1, minWidth: 0 }}>
            <span className="champ-libelle">Chercher dans le contenu des documents</span>
            <input className="saisie" type="search" value={saisie} onChange={(e) => setSaisie(e.target.value)}
              placeholder="Référence exacte, code défaut, mot-clé" autoComplete="off" spellCheck={false} />
          </label>
          <Bouton type="submit" variante="secondaire" icone={<Search aria-hidden />}>Chercher</Bouton>
        </form>
        <label className="case petit" style={{ marginTop: 4 }}>
          <input type="checkbox" checked={nonValides} onChange={(e) => changer({ nonValides: e.target.checked ? null : '0' })} />
          <span>Inclure les documents non validés (signalés comme tels)</span>
        </label>
      </Panneau>

      {termeRecherche && (
        <Panneau serre titre={recherche.data ? `Extraits trouvés pour « ${termeRecherche} »` : 'Recherche dans les extraits'}
          actions={<Bouton compact variante="fantome" onClick={() => changer({ q: null, ref: null })}>Effacer la recherche</Bouton>}>
          {recherche.isLoading && <Chargement lignes={3} libelle="Recherche dans les extraits…" />}
          {recherche.isError && <EtatErreur erreur={recherche.error} reessayer={() => recherche.refetch()} />}
          {recherche.data && recherche.data.resultats.length === 0 && (
            <EtatVide icone={<Search />} titre="Aucun extrait trouvé"
              texte={<>Aucun document {nonValides ? '' : 'validé '}ne contient « {termeRecherche} ». Vérifiez l’orthographe de la référence
                {nonValides ? '' : ', incluez les documents non validés'} ou importez la documentation du fabricant.</>} />
          )}
          {recherche.data && recherche.data.resultats.length > 0 && (
            <ol className="liste doc-extraits">
              {recherche.data.resultats.map((r) => (
                <li key={r.chunk_id}>
                  <Link className="liste-element doc-extrait" to={`/documentation/lecteur/${r.version_id}?page=${r.page_no}&extrait=${r.chunk_id}`}>
                    <span className="liste-element-corps">
                      <span className="rangee" style={{ gap: 8 }}>
                        <span className="liste-element-titre">{r.title}</span>
                        {r.exact && <Badge ton="accent">Référence exacte</Badge>}
                        {r.status !== 'valide' && <BadgeStatutDocument statut={r.status} />}
                      </span>
                      <span className="petit discret">
                        {[r.manufacturer, r.range_name, libelleType(r.doc_type), `version ${r.version_label}`].filter(Boolean).join(' · ')}
                        {' — '}p. {r.page_no}{r.locator ? `, ${r.locator}` : ''}
                      </span>
                      <span className="extrait"><Surligne texte={r.extrait} /></span>
                      {r.ocr && <MarqueIa />}
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          )}
        </Panneau>
      )}

      <Panneau serre titre={liste.data ? pluriel(liste.data.total, 'document') : 'Documents'}>
        <div className="doc-filtres">
          <ChampChoix libelle="Statut" value={statut} onChange={(e) => changer({ statut: e.target.value || null })} vide="Tous les statuts"
            options={['valide', 'a_valider', 'obsolete', 'rejete', 'illisible', 'en_traitement'].map((s) => ({ valeur: s, libelle: STATUTS_DOCUMENT[s].libelle }))} />
          <ChampChoix libelle="Fabricant" value={fabricant} onChange={(e) => changer({ fabricant: e.target.value || null })} vide="Tous les fabricants"
            options={(liste.data?.fabricants ?? (fabricant ? [fabricant] : [])).map((f) => ({ valeur: f, libelle: f }))} />
          <ChampChoix libelle="Type" value={type} onChange={(e) => changer({ type: e.target.value || null })} vide="Tous les types"
            options={TYPES_BIBLIOTHEQUE.map((t) => ({ valeur: t, libelle: TYPES_DOCUMENT[t] }))} />
          <label className="champ">
            <span className="champ-libelle">Référence exacte</span>
            <input className="saisie saisie-mono" value={ref} placeholder="Ex. ABC-1234" spellCheck={false}
              onChange={(e) => changer({ ref: e.target.value.trim() || null })} />
          </label>
        </div>
        {filtresActifs && (
          <div className="doc-filtres-actifs">
            <Bouton compact variante="fantome" onClick={() => changer({ ref: null, statut: null, fabricant: null, type: null })}>Retirer les filtres</Bouton>
          </div>
        )}

        {liste.isLoading && <Chargement lignes={4} />}
        {liste.isError && <EtatErreur erreur={liste.error} reessayer={() => liste.refetch()} />}
        {liste.data && docs.length === 0 && (
          filtresActifs
            ? <EtatVide icone={<Search />} titre="Aucun document ne correspond"
                texte={ref ? <>Aucun document ne cite la référence <span className="mono">{ref}</span>.</> : 'Modifiez ou retirez les filtres.'}
                action={peut('doc.importer') ? <LienBouton compact to={`/documentation/importer${ref ? `?ref=${encodeURIComponent(ref)}` : ''}`}>Importer un document</LienBouton> : undefined} />
            : <EtatVide icone={<BookOpen />} titre="La bibliothèque est vide"
                texte="Importez les notices, fiches produits et listes de codes défaut des équipements installés, depuis des sources autorisées."
                action={peut('doc.importer') ? <LienBouton variante="principal" to="/documentation/importer">Importer des documents</LienBouton> : undefined} />
        )}
        {docs.length > 0 && (
          <>
            <div className="tableau-conteneur doc-vue-large">
              <table className="tableau">
                <thead><tr>
                  <th scope="col">Titre</th><th scope="col">Fabricant et gamme</th><th scope="col">Références</th><th scope="col">Type</th>
                  <th scope="col">Version</th><th scope="col">Statut</th><th scope="col" className="expert-seulement">Dernière vérification</th>
                </tr></thead>
                <tbody>
                  {docs.map((d) => (
                    <tr key={d.id}>
                      <td className="doc-cellule-titre">
                        <Link to={`/documentation/${d.id}`}>{d.title}</Link>
                        {d.demo && <> <Badge ton="ambre" icone={<FlaskConical size={14} aria-hidden />}>Démo</Badge></>}
                      </td>
                      <td>{[d.manufacturer, d.range_name].filter(Boolean).join(' — ') || <span className="inconnu">Inconnu</span>}</td>
                      <td><RefsCourtes refs={d.product_refs} /></td>
                      <td>{libelleType(d.doc_type)}</td>
                      <td className="nowrap">{d.version_id
                        ? <Link to={`/documentation/lecteur/${d.version_id}`} aria-label={`Lire la version ${d.version_label} de ${d.title}`}>{d.version_label}</Link>
                        : '—'}{d.nb_versions > 1 && <span className="tres-petit discret"> ({d.nb_versions} versions)</span>}</td>
                      <td>{d.status ? <BadgeStatutDocument statut={d.status} /> : null}</td>
                      <td className="expert-seulement nowrap">{d.last_checked_at ? date(d.last_checked_at) : <span className="inconnu">Jamais</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="liste doc-vue-etroite">
              {docs.map((d) => (
                <li key={d.id}>
                  <Link className="liste-element" to={`/documentation/${d.id}`}>
                    <FileText aria-hidden className="discret" />
                    <span className="liste-element-corps">
                      <span className="liste-element-titre">{d.title}</span>
                      <span className="petit discret">{[d.manufacturer, d.range_name, libelleType(d.doc_type)].filter(Boolean).join(' · ')}</span>
                      {d.product_refs.length > 0 && <span className="petit"><RefsCourtes refs={d.product_refs} /></span>}
                      <span className="rangee" style={{ gap: 8 }}>
                        {d.status && <BadgeStatutDocument statut={d.status} />}
                        <span className="tres-petit discret">Version {d.version_label ?? '—'} · vérifié {d.last_checked_at ? `le ${date(d.last_checked_at)}` : 'jamais'}</span>
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
            {nbPages > 1 && (
              <div className="doc-pagination">
                <Bouton compact disabled={page <= 1} onClick={() => changer({ page: String(page - 1) })}>Page précédente</Bouton>
                <span className="petit secondaire">Page {page} sur {nbPages}</span>
                <Bouton compact disabled={page >= nbPages} onClick={() => changer({ page: String(page + 1) })}>Page suivante</Bouton>
              </div>
            )}
          </>
        )}
      </Panneau>
    </div>
  );
}

function RefsCourtes({ refs }: { refs: string[] }) {
  if (!refs.length) return <span className="inconnu">Aucune</span>;
  const vus = refs.slice(0, 3);
  return <span className="doc-refs">{vus.map((r) => <span key={r} className="mono doc-ref">{r}</span>)}{refs.length > 3 && <span className="tres-petit discret">et {refs.length - 3} autre(s)</span>}</span>;
}
